import { Box, useTheme } from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import ProjectParticipantCard from './ProjectParticipantCard';
import { ExoSkeleton } from '../ExoSkeleton';
import { ExoAutoCompleteSelector } from '../ExoAutoCompleteSelector';
import { API_PATCH, DBRequest, useConfig } from '../../../api/api';
import Loader from '../../special/Loader';
import { useState } from 'react';
import ExoDialog from '../ExoDialog';

const ProjectParticipantsList = ({
  participants,
  project,
  onProjectChange,
  editmode,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  function handleRemoveParticipant(participantData) {
    const updatedParticipants = participants.filter(
      (p) => p.id !== participantData.id
    );
    patchParticipants(updatedParticipants);
  }

  function handleAddParticipant(data, type) {
    const newParticipant = {
      type,
      participant: data,
      email:
        data.emails?.find((e) => e.isPrimary)?.emailAddress ||
        data.emails?.[0]?.emailAddress ||
        data.email ||
        null,
    };
    const updatedParticipants = [...participants, newParticipant];
    patchParticipants(updatedParticipants);
    setAddDialogOpen(false);
  }

  function patchParticipants(updatedParticipants) {
    DBRequest({
      config,
      path: `projects/${project.id}`,
      method: API_PATCH({ participants: updatedParticipants }),
      onResponse: onProjectChange,
      onLoading: setIsLoading,
    });
  }

  if (!participants)
    return (
      <Box className="overflow-y-auto max-h-[500px] xl:max-h-[9999px] xl:h-full flex flex-col gap-1">
        <ExoSkeleton size="115px" n={5} />
      </Box>
    );

  return (
    <Box className="flex flex-col gap-1 overflow-y-auto h-full pr-1">
      <Loader active={isLoading} />
      {participants.map((participantData) => (
        <ProjectParticipantCard
          key={participantData.id}
          participantData={participantData}
          onRemove={
            editmode
              ? () => handleRemoveParticipant(participantData)
              : undefined
          }
        />
      ))}
      {editmode && (
        <>
          <Box
            className="flex items-center justify-center rounded-lg cursor-pointer"
            onClick={() => setAddDialogOpen(true)}
            sx={{
              py: '6px',
              opacity: 0.3,
              transition: 'opacity 0.15s ease, background-color 0.15s ease',
              ':hover': {
                opacity: 0.7,
                bgcolor: colors.bgInput,
              },
            }}
          >
            <AddIcon sx={{ fontSize: '18px' }} />
          </Box>
          <ExoDialog
            open={addDialogOpen}
            onClose={() => setAddDialogOpen(false)}
            limitWidth
          >
            <Box className="p-4">
              <ExoAutoCompleteSelector
                label={t('Add Participant')}
                onSelectionChange={handleAddParticipant}
                multiselect={false}
                searchParams={['person_leads', 'company_leads']}
              />
            </Box>
          </ExoDialog>
        </>
      )}
    </Box>
  );
};

export default ProjectParticipantsList;
